import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Alert,
  useTheme,
} from '@mui/material';
import {
  Home,
  DeleteForever,
  FiberManualRecord,
  Warning,
  Lock,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';

const DeleteAccountPage = () => {
  const theme = useTheme();

  const sections = [
    {
      title: 'What happens when you delete your account?',
      icon: <DeleteForever sx={{ color: theme.palette.error.main }} />,
      items: [
        'Your account will be marked as deleted and you will no longer be able to sign in using your existing credentials.',
        'You will lose access to your profile, wishlist, saved addresses, and other account features.',
        'Your purchase history, order history, and account-related information will no longer be accessible through the application.',
        'Any active sessions will be terminated.',
      ],
    },
    {
      title: 'Data Retention',
      icon: <Lock sx={{ color: theme.palette.primary.main }} />,
      items: [
        'Certain account and transaction records may be retained for legal, regulatory, security, fraud prevention, auditing, and business record-keeping purposes.',
        'Retained data will not be available to you through the application after account deletion.',
        'Retained information will be handled in accordance with our Privacy Policy and applicable laws.',
      ],
    },
    {
      title: 'Important',
      icon: <Warning sx={{ color: theme.palette.warning.main }} />,
      items: [
        'Account deletion cannot be undone.',
        'To use our services again in the future, you may need to create a new account, subject to our policies.',
        'Please ensure you understand the consequences before proceeding.',
      ],
    },
  ];

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none' }}
          >
            <Home fontSize="small" />
            Home
          </Link>
          <Typography color="text.primary">Delete Account</Typography>
        </Breadcrumbs>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.error.dark} 0%, ${theme.palette.error.main} 100%)`,
              color: '#fff',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <DeleteForever sx={{ fontSize: 40 }} />
              <Typography variant="h4" fontWeight={700}>
                Delete Your Account
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: '#fff', opacity: 0.95 }}>
              We're sorry to see you go.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#fff', opacity: 1 }}>
              Deleting your account will permanently deactivate your access to our services. Please
              review the following information before proceeding.
            </Typography>
          </Paper>

          {/* Content Sections */}
          <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, mb: 3 }}>
            {sections.map((section, index) => (
              <Box key={section.title}>
                {index > 0 && <Divider sx={{ my: 4 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  {section.icon}
                  <Typography variant="h6" fontWeight={600}>
                    {section.title}
                  </Typography>
                </Box>
                <List disablePadding>
                  {section.items.map((item, i) => (
                    <ListItem key={i} alignItems="flex-start" disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                        <FiberManualRecord sx={{ fontSize: 8, color: theme.palette.text.secondary }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={item}
                        primaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Paper>

          {/* Proceed Section */}
          <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Proceed with Account Deletion
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              To continue, please sign in and verify your identity. After signing in, click{' '}
              <strong>Delete Account</strong> to permanently deactivate your account.
            </Typography>

            <Button
              component={RouterLink}
              to="/login"
              variant="contained"
              color="error"
              size="large"
              startIcon={<Lock />}
              sx={{ mb: 3, px: 4 }}
            >
              Login to Continue
            </Button>

            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              By clicking "Delete My Account", you acknowledge that you have read and understood the
              information above.
            </Alert>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default DeleteAccountPage;
